/**
 * Core-100 R2 — CORE-L: how a credential's trust and state are SAID.
 *
 * Three trust states, never flattened into one badge, and never worded as doubt: an unverified
 * credential is "self-reported", not "unverified" or "unconfirmed" (absence of verification is not
 * evidence of falsity). OCR is never called verification. State over time mirrors the SQL
 * `certification_status`: an expired credential was held; a revoked one existed.
 */
export type CertificationTrustState = 'person_asserted' | 'document_supported' | 'directly_attested';
export type CertificationStatus = 'revoked' | 'superseded' | 'expired' | 'expiring' | 'current' | 'no_expiry';

export function describeTrust(
  state: CertificationTrustState | null | undefined,
  opts: { assessmentMatches?: boolean | null; attestedByKind?: string | null } = {},
): string {
  switch (state ?? 'person_asserted') {
    case 'directly_attested':
      return opts.attestedByKind === 'school' ? 'Attested by the school'
        : opts.attestedByKind === 'issuer' ? 'Attested by the issuer'
        : 'Attested through OptiBay';
    case 'document_supported':
      return opts.assessmentMatches === false
        ? 'Document on file · details edited after reading'
        : 'Document on file · read by OptiBay';
    default:
      return 'Self-reported';
  }
}

/**
 * Mirror of SQL certification_status + certification_expiry_state (dates are calendar dates, compared as
 * YYYY-MM-DD). Core-100 R3 (OD-11): 'expiring' exists only when the platform's expiring window is SET; while it is
 * null (the default, pending the owner's ruling) no credential is ever called expiring — no period is invented.
 */
export function certificationStatus(
  expiresDate: string | null | undefined,
  revokedAt: string | null | undefined,
  supersededBy: string | null | undefined,
  todayIso: string,
  expiringWindowDays: number | null = null,
): CertificationStatus {
  if (revokedAt) return 'revoked';
  if (supersededBy) return 'superseded';
  if (!expiresDate) return 'no_expiry';
  const expires = expiresDate.slice(0, 10);
  const today = todayIso.slice(0, 10);
  if (expires < today) return 'expired';
  if (expiringWindowDays != null && expiringWindowDays > 0) {
    const horizon = new Date(`${today}T00:00:00Z`);
    horizon.setUTCDate(horizon.getUTCDate() + expiringWindowDays);
    if (expires <= horizon.toISOString().slice(0, 10)) return 'expiring';
  }
  return 'current';
}

/**
 * Core-100 R4 (CORE-L): the person may delete their own assertion, never a directly-attested or a revoked credential
 * (server: trg_guard_passport_certification_delete, proof core_l_certifications L36-L39). They hide it instead.
 */
export function isCertificationErasable(cert: { trust_state?: string | null; revoked_at?: string | null }): boolean {
  return cert.trust_state !== 'directly_attested' && !cert.revoked_at;
}

/**
 * Core-100 R4 (CORE-L Rel): "today" for a credential's state is the VIEWER's local calendar date, not the UTC date
 * (a credential expiring today must not read "Expired" in the evening of a UTC-negative timezone).
 */
export function localTodayIso(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function describeStatus(status: CertificationStatus): string | null {
  switch (status) {
    case 'revoked': return 'Attestation revoked';
    case 'superseded': return 'Superseded by a newer entry';
    case 'expired': return 'Expired';
    case 'expiring': return 'Expires soon';
    default: return null;
  }
}
